import type { ContractReadModelRead } from '@mehir/api/contract-api';
import type { CurrencyViewModel } from '@mehir/api/finance-api';

// Fields present in the live API response but missing from the generated
// TypeScript types. Extend defensively until the swagger is regenerated.
export type ContractWithExtras = ContractReadModelRead & {
  originalAmount?: number | null;
  approvedDate?: string | null;
};

export const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  waiting: { label: 'Beklemede', variant: 'outline' },
  approved: { label: 'Onaylandı', variant: 'default' },
  completed: { label: 'Tamamlandı', variant: 'secondary' },
  rejected: { label: 'Reddedildi', variant: 'destructive' },
};

export const getStatusLabel = (status: string | undefined): string => {
  return statusConfig[status || '']?.label || status || '-';
};

export const getStatusVariant = (status: string | undefined): 'default' | 'secondary' | 'destructive' | 'outline' => {
  return statusConfig[status || '']?.variant || 'outline';
};

export const getMemberName = (contract: ContractReadModelRead): string => {
  const member = contract.invitingMember || contract.userMember;
  if (!member) return '-';
  const info = member.personalInfo;
  if (info?.name || info?.surname) return [info.name, info.surname].filter(Boolean).join(' ');
  return member.userId || '-';
};

export const getInvitingName = (contract: ContractReadModelRead): string => {
  const info = contract.invitingMember?.personalInfo;
  if (!info) return '-';
  return [info.name, info.surname].filter(Boolean).join(' ').trim() || '-';
};

export const getInvitedName = (contract: ContractReadModelRead): string => {
  const info = contract.invitedMember?.personalInfo;
  if (!info) return '-';
  return [info.name, info.surname].filter(Boolean).join(' ').trim() || '-';
};

// Legacy helper — kept for callers that genuinely want a TRY-formatted number
// without currency conversion. Prefer formatTL() / formatNativeAmount() going
// forward.
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
};

export const getCompletionPercentage = (totalAmount: number | null | undefined, remainingAmount: number | null | undefined): number => {
  if (!totalAmount || totalAmount <= 0) return 0;
  const remaining = remainingAmount ?? 0;
  return Math.min(100, Math.max(0, Math.round(((totalAmount - remaining) / totalAmount) * 100)));
};

export const payingTypeLabels: Record<string, string> = {
  wallet: 'Cüzdan',
  manuel: 'Manuel',
  card: 'Kart',
  bank_transfer: 'Banka Transferi',
  gold_transfer: 'Altın Transferi',
  silver_transfer: 'Gümüş Transferi',
};

// ---------------------------------------------------------------------------
// Currency / amount handling
// ---------------------------------------------------------------------------

// Maps contract Currency enum to the currencies endpoint `code` field.
const CURRENCY_RATE_CODE: Record<string, string> = {
  gold: 'ALTIN/GRAM',
  silver: 'GUMUS/GRAM',
  usd: 'USD/TL',
  eur: 'EUR/TL',
  // gbp: backend currently doesn't return a GBP rate in the response we have
};

// Short suffix shown next to native (non-TL) amounts.
const CURRENCY_NATIVE_SUFFIX: Record<string, string> = {
  try: '₺',
  usd: '$',
  eur: '€',
  gbp: '£',
  gold: 'gr altın',
  silver: 'gr gümüş',
};

// Turkish names, for warnings that have to say which currency is at fault.
const CURRENCY_LABEL: Record<string, string> = {
  try: 'lira',
  usd: 'dolar',
  eur: 'euro',
  gbp: 'sterlin',
  gold: 'altın',
  silver: 'gümüş',
};

const currencyLabel = (currency: string): string => CURRENCY_LABEL[currency] ?? currency;

/**
 * Effective currency of a contract, or null when the record does not say.
 * - For Gold/Silver/Cash mehir types: `mehirType.currency`.
 * - For Other types (currency === 'none'): the last revision's currency
 *   (Arsa: 'try', Araç: 'usd' in the sample data).
 * - Null when neither is present — the record genuinely does not tell us.
 *
 * Use this wherever guessing would corrupt a number. `getContractCurrency`
 * is the display-side variant.
 */
export const resolveContractCurrency = (contract: ContractWithExtras): string | null => {
  const mehirCurrency = contract.mehirType?.currency;
  if (mehirCurrency && mehirCurrency !== 'none') return mehirCurrency;
  return contract.lastRevision?.currency || null;
};

/**
 * Display-side currency: falls back to TRY so a label never renders blank.
 *
 * Safe for formatting, not for arithmetic — a contract whose currency cannot
 * be resolved would have its raw amount counted as lira. Aggregation uses
 * `resolveContractCurrency` and excludes those records instead.
 */
export const getContractCurrency = (contract: ContractWithExtras): string =>
  resolveContractCurrency(contract) ?? 'try';

/**
 * The "native count" the user originally entered when creating the contract
 * (e.g. 500 Reşat coins, 25.000 EUR). Uses `originalAmount` when the backend
 * provides it; otherwise derives it from `totalAmount / mehirType.ratio`.
 */
export const getOriginalAmount = (contract: ContractWithExtras): number | null => {
  if (contract.originalAmount != null) return contract.originalAmount;
  const ratio = contract.mehirType?.ratio;
  if (contract.totalAmount != null && ratio && ratio > 0) {
    return contract.totalAmount / ratio;
  }
  return contract.totalAmount ?? null;
};

/**
 * Derive the "native remaining" by applying the same ratio as the total.
 * The backend only gives us `remainingAmount` in the base unit (e.g. grams),
 * so we scale it back to the native unit so the dual display stays consistent.
 */
export const getOriginalRemaining = (contract: ContractWithExtras): number | null => {
  if (contract.remainingAmount == null) return null;
  const ratio = contract.mehirType?.ratio;
  if (ratio && ratio > 0) return contract.remainingAmount / ratio;
  return contract.remainingAmount;
};

/**
 * Convert an amount in a known currency to TRY using the buyingRate from
 * the finance currencies endpoint. Returns null when:
 * - amount is missing, OR
 * - currencies list isn't loaded, OR
 * - the currency isn't recognised / no rate found.
 *
 * `try` is short-circuited to 1:1.
 */
export const convertToTL = (
  amount: number | null | undefined,
  currency: string,
  currencies: CurrencyViewModel[] | undefined,
): number | null => {
  if (amount == null) return null;
  if (currency === 'try') return amount;
  if (!currencies?.length) return null;
  const code = CURRENCY_RATE_CODE[currency];
  if (!code) return null;
  const rate = currencies.find((c) => c.code === code)?.buyingRate;
  if (!rate) return null;
  return amount * rate;
};

/**
 * Format a number with Turkish locale, capped at 2 fraction digits.
 */
const formatNumber = (value: number, maxDigits = 2): string =>
  new Intl.NumberFormat('tr-TR', { maximumFractionDigits: maxDigits }).format(value);

/**
 * "Native" amount display:
 * - Gold/Silver mehir types → "500 Reşat Lira" (uses mehirType.name, since each
 *   gold variant has its own coin name).
 * - Cash mehir types        → "25.000 €" (currency symbol).
 * - Other types             → suffixed with the resolved currency symbol.
 *
 * When `value` already represents the base unit (e.g. grams), pass it directly;
 * when it represents the native count, prefer this helper too — the display
 * label adapts based on whether the contract has a named mehir type.
 */
export const formatNativeAmount = (
  value: number | null | undefined,
  contract: ContractWithExtras,
  { useMehirTypeName = true }: { useMehirTypeName?: boolean } = {},
): string => {
  if (value == null) return '-';
  const formatted = formatNumber(value);
  const mehirType = contract.mehirType;
  const mainTypeName = mehirType?.mainType?.typeName;

  // Gold / Silver — mehir type carries a coin name we should use
  if (useMehirTypeName && (mainTypeName === 'Gold' || mainTypeName === 'Silver') && mehirType?.name) {
    return `${formatted} ${mehirType.name}`;
  }

  const currency = getContractCurrency(contract);
  const suffix = CURRENCY_NATIVE_SUFFIX[currency];
  return suffix ? `${formatted} ${suffix}` : formatted;
};

/**
 * Format a base-unit amount with its Turkish unit suffix. Contract
 * `totalAmount`/`remainingAmount` come in base units (grams for gold/silver,
 * the currency itself for FX/TRY), so this renders "350 gr altın", "25.000 $".
 */
export const formatUnitAmount = (
  value: number | null | undefined,
  currency: string,
): string => {
  if (value == null) return '-';
  const suffix = CURRENCY_NATIVE_SUFFIX[currency];
  const formatted = formatNumber(value);
  return suffix ? `${formatted} ${suffix}` : formatted;
};

/**
 * Format a TRY amount. Hides decimals for big numbers by default.
 */
export const formatTL = (
  amount: number | null | undefined,
  { maximumFractionDigits = 0, approximate = false }: { maximumFractionDigits?: number; approximate?: boolean } = {},
): string => {
  if (amount == null) return '-';
  const formatted = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits,
  }).format(amount);
  return approximate ? `≈ ${formatted}` : formatted;
};

/**
 * Convert a TRY amount to USD using the live USD/TL buying rate.
 * Returns null when the rate isn't available.
 */
export const convertTLToUSD = (
  tl: number | null | undefined,
  currencies: CurrencyViewModel[] | undefined,
): number | null => {
  if (tl == null) return null;
  const usdRate = convertToTL(1, 'usd', currencies); // TL value of 1 USD
  if (!usdRate) return null;
  return tl / usdRate;
};

/**
 * Format a USD amount (en-US locale). Hides decimals for big numbers by default.
 */
export const formatUSD = (
  amount: number | null | undefined,
  { maximumFractionDigits = 0, approximate = false }: { maximumFractionDigits?: number; approximate?: boolean } = {},
): string => {
  if (amount == null) return '-';
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits,
  }).format(amount);
  return approximate ? `≈ ${formatted}` : formatted;
};

/**
 * Convert a contract's amount (in its base unit) to TRY. Pass `totalAmount` or
 * `remainingAmount` — both are in the same base currency.
 */
export const convertContractAmountToTL = (
  amount: number | null | undefined,
  contract: ContractWithExtras,
  currencies: CurrencyViewModel[] | undefined,
): number | null => convertToTL(amount, getContractCurrency(contract), currencies);

/** Why a contract could not be counted towards the TL totals. */
export type ExclusionReason =
  | 'missing-amount'
  | 'unknown-currency'
  | 'unsupported-currency'
  | 'missing-rate';

export interface ExclusionBucket {
  reason: ExclusionReason;
  count: number;
  /** Distinct currencies involved, sorted. Empty for 'missing-amount'. */
  currencies: string[];
}

export interface TLTotals {
  totalTL: number;
  paidTL: number;
  remainingTL: number;
  excluded: { count: number; buckets: ExclusionBucket[] };
}

/**
 * Why this contract cannot be converted, or null when it can.
 *
 * Kept separate from `convertToTL` because the totals need the reason, not
 * just the failure: "no rate for sterling" and "this record has no amount at
 * all" are different problems and were being reported as the same one.
 */
const classifyContract = (
  contract: ContractWithExtras,
  currencies: CurrencyViewModel[] | undefined,
): { reason: ExclusionReason; currency: string | null } | null => {
  if (contract.totalAmount == null) return { reason: 'missing-amount', currency: null };

  const currency = resolveContractCurrency(contract);
  if (!currency) return { reason: 'unknown-currency', currency: null };
  if (currency === 'try') return null;

  const code = CURRENCY_RATE_CODE[currency];
  if (!code) return { reason: 'unsupported-currency', currency };

  const rate = currencies?.find((c) => c.code === code)?.buyingRate;
  if (!rate) return { reason: 'missing-rate', currency };

  return null;
};

/**
 * Aggregate TL totals across a set of contracts.
 *
 * Contracts that cannot be converted are left out of the totals and reported
 * in `excluded`, grouped by why — so the warning can name the cause instead of
 * blaming the currency for everything, including records that simply have no
 * amount.
 */
export const aggregateTLTotals = (
  contracts: ContractWithExtras[],
  currencies: CurrencyViewModel[] | undefined,
): TLTotals => {
  let totalTL = 0;
  let paidTL = 0;
  let remainingTL = 0;
  const excluded = new Map<ExclusionReason, { count: number; currencies: Set<string> }>();

  for (const c of contracts) {
    const failure = classifyContract(c, currencies);
    if (failure) {
      const bucket = excluded.get(failure.reason) ?? { count: 0, currencies: new Set<string>() };
      bucket.count++;
      if (failure.currency) bucket.currencies.add(failure.currency);
      excluded.set(failure.reason, bucket);
      continue;
    }

    // classifyContract has already proved these resolve.
    const total = convertContractAmountToTL(c.totalAmount, c, currencies) as number;
    const remaining = convertContractAmountToTL(c.remainingAmount, c, currencies);

    totalTL += total;
    if (remaining != null) {
      remainingTL += remaining;
      paidTL += total - remaining;
    } else {
      // Whole `total` is treated as unpaid when remaining is missing.
      remainingTL += total;
    }
  }

  // Kuruş-level rounding, applied once at the end: summing hundreds of
  // converted amounts otherwise drifts into values like 1234.5600000000002.
  const toKurus = (value: number) => Math.round(value * 100) / 100;

  // Stable order so the warning sentence does not reshuffle between renders.
  const REASON_ORDER: ExclusionReason[] = [
    'unsupported-currency',
    'missing-rate',
    'unknown-currency',
    'missing-amount',
  ];

  const buckets = REASON_ORDER.filter((reason) => excluded.has(reason)).map((reason) => {
    const bucket = excluded.get(reason) as { count: number; currencies: Set<string> };
    return {
      reason,
      count: bucket.count,
      currencies: [...bucket.currencies].sort(),
    };
  });

  return {
    totalTL: toKurus(totalTL),
    paidTL: toKurus(paidTL),
    remainingTL: toKurus(remainingTL),
    excluded: {
      count: buckets.reduce((sum, bucket) => sum + bucket.count, 0),
      buckets,
    },
  };
};

/**
 * One sentence naming why records were left out, or null when none were.
 *
 * The old wording said "N sözleşmenin para birimi çevrilemedi" for every
 * cause, including contracts that had no amount at all — which sent people
 * looking at currency configuration for a data-entry problem.
 */
export const describeExcludedContracts = (
  excluded: TLTotals['excluded'],
): string | null => {
  if (excluded.count === 0) return null;

  const named = (currencies: string[]) =>
    currencies.length > 0 ? ` (${currencies.map(currencyLabel).join(', ')})` : '';

  const parts = excluded.buckets.map(({ reason, count, currencies }) => {
    switch (reason) {
      case 'unsupported-currency':
        return `${count} sözleşme desteklenmeyen para biriminde${named(currencies)}`;
      case 'missing-rate':
        return `${count} sözleşme için güncel kur alınamadı${named(currencies)}`;
      case 'unknown-currency':
        return `${count} sözleşmenin para birimi belirlenemedi`;
      case 'missing-amount':
        return `${count} sözleşmenin tutarı girilmemiş`;
    }
  });

  return `${parts.join('; ')} — toplamlar bu kayıtları içermiyor.`;
};
